import { computed, Ref } from "vue";
import { useRouter } from "vue-router";
import { AccessAction, AccessResource } from "@/shared/access-control/access-control.ts";
import { useI18n } from "vue-i18n";
import { CommonSearchTabAliases } from "@/shared/environment/common-route-aliases.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { MenuEntry } from "@/shared/components/menu/menu.model.ts";
import ShareMenuEntry from "@/shared/share/share-menu-entry.vue";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";

export function useHeaderMenuEntries(
    searchTabAlias: Ref<CommonSearchTabAliases | undefined>,
    product?: Ref<string | undefined>
) {
    const router = useRouter();
    const { t } = useI18n();
    const { isXSmall } = useScreenSize();

    const { hasAccess: hasConfigAccess } = useHasAccess({
        resource: AccessResource.activities,
        action: AccessAction.write,
    });

    const { hasAccess: hasFilterConfigAccess } = useHasAccess({
        resource: AccessResource.facet_collections,
        action: AccessAction.write,
    });

    const menuEntries = computed<MenuEntry[]>(() => {
        if (!searchTabAlias.value) return [];

        const configureViewEntry = {
            id: "configure-view",
            text: t("shared.Configure view"),
            icon: "fa-regular fa-sliders-simple icon-color",
            visible: hasConfigAccess.value,
        };

        const configureFilterEntry = {
            id: "configure-filter",
            text: t("shared.Configure filter"),
            icon: "fa-regular fa-sliders-simple icon-color",
            visible: hasFilterConfigAccess.value,
        };

        let entries: MenuEntry[];

        switch (searchTabAlias.value) {
            case CommonSearchTabAliases.activities:
                entries = [
                    {
                        ...configureViewEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/datadisplay/activities-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "more-menu-configure-activities-view" },
                    },
                    {
                        ...configureFilterEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/filter/activities-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "more-menu-configure-activities-filter" },
                    },
                ];
                break;
            case CommonSearchTabAliases.assets:
                entries = [
                    {
                        ...configureViewEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/datadisplay/asset-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "asset-settings-toolbar-configure-button" },
                    },
                    {
                        ...configureFilterEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/filter/asset-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "asset-settings-toolbar-configure-filter-button" },
                    },
                ];
                break;
            case CommonSearchTabAliases.products:
                entries = [
                    {
                        ...configureViewEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/datadisplay/product-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "product-settings-toolbar-configure-button" },
                    },
                    {
                        ...configureFilterEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/filter/product-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "product-settings-toolbar-configure-filter-button" },
                    },
                ];
                break;
            case CommonSearchTabAliases.documentations:
                entries = [
                    {
                        ...configureViewEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/datadisplay/document-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "more-menu-configure-document-selection-view" },
                    },
                    {
                        ...configureFilterEntry,
                        click: () => {
                            const route = router.resolve({ path: "/admin/filter/document-selection" });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "more-menu-configure-document-filter" },
                    },
                ];
                break;
            case CommonSearchTabAliases.commonArticles:
                entries = [
                    {
                        ...configureViewEntry,
                        click: () => {
                            const route = router.resolve({
                                path: product?.value
                                    ? "/admin/datadisplay/parts"
                                    : "/admin/datadisplay/article-selection",
                            });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "article-settings-toolbar-configure-button" },
                    },
                    {
                        ...configureFilterEntry,
                        click: () => {
                            const route = router.resolve({
                                path: product?.value ? "/admin/filter/parts" : "/admin/filter/article-selection",
                            });
                            window.open(route.href, "_blank");
                        },
                        elementAttrs: { "data-qs": "article-settings-toolbar-configure-filter-button" },
                    },
                ];
                break;
            default:
                return [];
        }

        if (isXSmall.value) {
            entries.push({
                id: "share-entry",
                component: {
                    getComponent: () => ShareMenuEntry,
                },
            });
        }

        return entries;
    });

    return { menuEntries };
}
